/**
 * Updates Overview: the archive-wide search box, then headline counts, recent activity
 * and the highlighted-need feed.
 *
 * This was the landing page until the Dashboard was built (`pages/Dashboard.jsx`), which
 * is what the brand lockup and the first item in the sidebar now open. It kept its own
 * entry under Overview because of the search box: that box is the only way into
 * archive-wide search, and `/` pressed on any page still sends the cursor here.
 */
import { useState, useEffect } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { PageHeader } from '../App'
import { useApi } from '../lib/api'
import { useCanBrowse } from '../lib/viewmode'
import { TrendChart } from '../components/charts'
import SearchResults from '../components/SearchResults'
import {
  Card, Tile, Badge, Empty, QueryState, Notice, SearchInput,
  fmtInt, relDays, PartnerLabel,
} from '../components/ui'
import { Icon } from '../components/Icon'
import { useCode } from '../lib/terms'

/** So `/` can put the cursor here from any page — see the handler in App. */
export const SEARCH_INPUT_ID = 'global-search'

/**
 * One tick a year for a monthly series: the first month present in each year, so a
 * year missing its January is still marked. The axis then reads 2021…2026 rather
 * than a run of 2022-06, 2022-11 that says the same thing twice.
 */
const yearTicks = (monthly) => {
  const seen = new Set()
  return monthly
    .filter((r) => {
      const year = String(r.month).slice(0, 4)
      if (seen.has(year)) return false
      seen.add(year)
      return true
    })
    .map((r) => r.month)
}

export default function UpdatesOverview({ user }) {
  const code = useCode()
  // A view-only account gets the same numbers and the same feeds. The countries are the
  // one thing on this page that is not part of that view, so that tile becomes a plain
  // figure rather than a link to a locked door — as does the data-quality notice, which
  // is a job for somebody who can act on it.
  const canBrowse = useCanBrowse()
  const query = useApi('stats-overview', 'stats/overview')
  const [params, setParams] = useSearchParams()
  const [input, setInput] = useState(params.get('q') ?? '')
  const [q, setQ] = useState(params.get('q') ?? '')

  // debounce so typing does not fire a query per keystroke
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(input.trim())
      const next = new URLSearchParams()
      if (input.trim()) next.set('q', input.trim())
      setParams(next, { replace: true })
    }, 350)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input])

  // `/` pressed on another page sends you here asking for the box; pressed on this
  // one it focuses the box where it already is, without a navigation.
  const { state } = useLocation()
  useEffect(() => {
    if (state?.focusSearch) document.getElementById(SEARCH_INPUT_ID)?.focus()
  }, [state])

  // Two characters is what the index needs to answer with anything but noise, and
  // below that the dashboard is what you came to the dashboard for.
  const searching = q.length > 1

  return (
    <PageHeader
      title="Updates Overview"
      sub="Search every update, then the archive's headline figures and latest activity"
      actions={
        user.caps.create ? (
          <Link className="btn btn-sm btn-primary" to="/newsletters/new">
            + New Update
          </Link>
        ) : null
      }
    >
      <div className="col">
        {/* First thing on the page, above the numbers: searching the archive is the
            most common reason to open it, and it used to cost a trip to a tab of
            its own. Typing hands the page over to the results; clearing the box
            gives the dashboard back. */}
        <SearchInput
          id={SEARCH_INPUT_ID}
          value={input}
          onChange={setInput}
          placeholder="Search every update and prayer request…"
          title="Press / from any page to search"
          aria-label="Search everything"
        />

        {searching ? (
          <SearchResults q={q} />
        ) : (
        <QueryState query={query}>
          {(d) => {
          const t = d.totals
          return (
            <div className="col">
              <div className="grid grid-stats">
                <Tile label="Updates" value={fmtInt(t.newsletters)} note={t.first_date ? `Since ${String(t.first_date).slice(0, 4)}` : null} to="/newsletters" />
                {/* The updates and the directory are in every view, so these two tiles
                    always lead somewhere. */}
                <Tile label="Partner Units" value={fmtInt(t.current_partners)} note={`${fmtInt(t.alumni_partners)} alumni`} to="/partners" />
                <Tile label="Country / category" value={fmtInt(t.categories)} note="Countries & locations" to={canBrowse ? '/countries' : undefined} />
              </div>

              {canBrowse && t.orphan_partners > 0 && (
                <Notice tone="warn" icon="warning" title="Codes missing from the partner unit registry">
                  {t.orphan_partners} S4CA code
                  {t.orphan_partners === 1 ? '' : 's'} appear in updates but not in the registry
                  file, so they have placeholder partner unit records.{' '}
                  <Link to="/data-quality">Review on the data quality page</Link>.
                </Notice>
              )}

              {/* Full width, so five years of months have room to read one by one. */}
              <Card title="Updates per Month" sub="Last five years">
                {d.monthly.length ? (
                  <TrendChart
                    data={d.monthly}
                    x="month"
                    series={[{ key: 'n', label: 'Updates' }]}
                    area
                    height={120}
                    xTicks={yearTicks(d.monthly)}
                    xFormat={(m) => String(m).slice(0, 4)}
                  />
                ) : (
                  <Empty title="No recent updates" />
                )}
              </Card>

              <div className="grid grid-2">
                <Card
                  title="Latest Updates"
                  actions={<Link className="btn btn-sm" to="/newsletters">All</Link>}
                >
                  <div className="table-wrap flush">
                    <table className="data compact">
                      <tbody>
                        {d.recent.map((r) => (
                          <tr key={r.id}>
                            <td className="nowrap">
                              <Link to={`/newsletters/${r.id}`} className="mono">
                                {r.published_on}
                              </Link>
                            </td>
                            <td>
                              <PartnerLabel id={r.partner_id} code={r.s4ca_code} name={r.partner_name} />
                            </td>
                            <td className="cell-snippet-cell">
                              <div className="cell-snippet">{r.snippet}</div>
                            </td>
                            <td>{r.highlighted_need ? <Badge tone="warn"><Icon name="star" size={12} /></Badge> : null}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                <Card
                  title="Highlighted Updates"
                  sub="Most recent flagged records"
                  actions={<Link className="btn btn-sm" to="/highlighted-needs">All</Link>}
                >
                  {d.highlighted.length ? (
                    <div className="col" style={{ gap: 12 }}>
                      {/* The whole block is the link, not the date inside it. Every
                          part of it names the same update, so every part of it should
                          open that update — and the snippet, which is the bit a reader
                          is actually reading when they decide to go there, was the one
                          part that did nothing. The date is a span now: an anchor
                          inside an anchor is not something HTML allows. */}
                      {d.highlighted.map((r) => (
                        <Link
                          key={r.id}
                          to={`/newsletters/${r.id}`}
                          className="hl-item"
                          aria-label={`Update dated ${r.published_on} from ${code(r.s4ca_code)}`}
                        >
                          <div className="row row-tight small">
                            <span className="mono hl-item-date">{r.published_on}</span>
                            <span className="subtle">{relDays(r.published_on)}</span>
                            <span className="subtle">·</span>
                            <span className="muted">{code(r.s4ca_code)}</span>
                            <span className="subtle">{r.country_name}</span>
                          </div>
                          <div className="small muted cell-snippet">{r.snippet}</div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <Empty icon="star" title="Nothing flagged" />
                  )}
                </Card>
              </div>
            </div>
          )
        }}
        </QueryState>
        )}
      </div>
    </PageHeader>
  )
}
